import React, { useState, useEffect, useMemo } from 'react';
import { ConfigTemplate, ConfigTemplateUpdate, DeviceType, JsonConfig, CustomField, getPriorityLevel } from '../../types/configTemplate';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { typesService, HardwareFieldDefinition } from '../../services/typesService';
import { getTypeBadgeColor } from '../../utils/fieldTypes';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { DeviceTypeSelector } from '../common/DeviceTypeSelector';
import { DeviceSpecificFields } from '../common/DeviceSpecificFields';
import { Client } from '../../types/client';
import AuditHistoryTab from '../audit/AuditHistoryTab';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useJsonConfigVisible } from '../../hooks/useJsonConfigVisible';
import { TemplateImpactPreviewModal } from './TemplateImpactPreviewModal';
import { ClientTargetingInput, DEFAULT_CLIENT_ID } from './ClientTargetingInput';

interface ConfigTemplateDetailModalProps {
  templateId: string | null;
  isOpen: boolean;
  canEdit?: boolean;
  isSuperAdmin?: boolean;
  onClose: () => void;
  onTemplateUpdated: () => void;
}

interface FormErrors {
  name?: string;
  priority?: string;
  [key: string]: string | undefined;
}

/* ── Read-only config fields table (matches device-types Field Defs style) ── */

const SENSITIVE_KEYS = new Set(['password', 'mqtt_pass']);
const KNOWN_SCALAR_GROUPS: Record<string, { label: string; keys: string[] }> = {
  syslog:       { label: 'Syslog',        keys: ['syslog_host', 'syslog_port', 'syslog_facility'] },
  mqtt:         { label: 'MQTT',           keys: ['mqtt_host', 'mqtt_port', 'mqtt_user', 'mqtt_pass'] },
  general:      { label: 'General',        keys: ['provisioning', 'keepalive_timer', 'password', 'generate_password'] },
};

const inferType = (key: string, value: unknown): string => {
  if (key.includes('port')) return 'port';
  if (key.includes('host')) return 'hostname';
  if (key === 'provisioning') return 'url';
  if (SENSITIVE_KEYS.has(key)) return 'password';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'object' && value !== null) return 'json';
  return 'string';
};

const TYPE_COLORS: Record<string, string> = {
  string: '#6b7280', number: '#3b82f6', boolean: '#10b981', password: '#ef4444',
  url: '#06b6d4', hostname: '#8b5cf6', port: '#f97316', json: '#f59e0b',
};

const ConfigFieldsTable: React.FC<{
  jsonConfig?: JsonConfig | null;
  customFields: CustomField[];
  canRevealPassword?: boolean;
}> = ({ jsonConfig, customFields, canRevealPassword }) => {
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());
  const config = jsonConfig || {};

  const toggleReveal = (key: string) =>
    setRevealedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Build grouped rows from scalar fields
  const assignedKeys = new Set(Object.values(KNOWN_SCALAR_GROUPS).flatMap(g => g.keys));
  const extraKeys = Object.keys(config).filter(k => k !== 'custom_fields' && !assignedKeys.has(k));

  const groups: { label: string; rows: { key: string; value: unknown }[] }[] = [];
  for (const [, grp] of Object.entries(KNOWN_SCALAR_GROUPS)) {
    const rows = grp.keys
      .filter(k => config[k] !== undefined)
      .map(k => ({ key: k, value: config[k] }));
    if (rows.length > 0) groups.push({ label: grp.label, rows });
  }
  if (extraKeys.length > 0) {
    groups.push({ label: 'Other', rows: extraKeys.map(k => ({ key: k, value: config[k] })) });
  }
  if (customFields.length > 0) {
    groups.push({
      label: 'Custom Fields',
      rows: customFields.map(cf => ({ key: cf.name, value: cf.value })),
    });
  }

  const formatValue = (key: string, value: unknown): string => {
    if (key === 'generate_password' && value === true) return 'random (12-char)';
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value) || '—';
  };

  if (groups.length === 0) {
    return <div className="empty-fields"><p>No configuration defined.</p></div>;
  }

  return (
    <div className="field-defs-list">
      {groups.map(group => (
        <div key={group.label} className="field-group">
          <h4 className="group-header">{group.label}</h4>
          <table className="field-defs-table">
            <thead>
              <tr>
                <th>Field Name</th>
                <th>Type</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {group.rows.map(({ key, value }) => {
                const type = inferType(key, value);
                const isSensitive = SENSITIVE_KEYS.has(key);
                const isRevealed = revealedKeys.has(key);
                return (
                  <tr key={key}>
                    <td><code>{key}</code></td>
                    <td>
                      <span className="type-badge" style={{ backgroundColor: TYPE_COLORS[type] || '#6b7280' }}>
                        {type}
                      </span>
                    </td>
                    <td>
                      {isSensitive && value ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span className="monospace">
                            {canRevealPassword && isRevealed ? formatValue(key, value) : '••••••••'}
                          </span>
                          {canRevealPassword && (
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={() => toggleReveal(key)}
                              title={isRevealed ? 'Hide' : 'Reveal'}
                              style={{ width: 22, height: 22 }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isRevealed ? (
                                  <>
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                    <line x1="1" y1="1" x2="23" y2="23"/>
                                  </>
                                ) : (
                                  <>
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                    <circle cx="12" cy="12" r="3"/>
                                  </>
                                )}
                              </svg>
                            </button>
                          )}
                        </span>
                      ) : (
                        <span className="monospace">{formatValue(key, value)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export const ConfigTemplateDetailModal: React.FC<ConfigTemplateDetailModalProps> = ({
  templateId,
  isOpen,
  canEdit = false,
  isSuperAdmin = false,
  onClose,
  onTemplateUpdated
}) => {
  const { formatDateTime, t } = useI18n();
  const [template, setTemplate] = useState<ConfigTemplate | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'config' | 'clients' | 'json' | 'history'>('overview');
  const { canViewAuditLog } = useClientAccess();
  // Gate the "JSON Config" tab the same way the RAW tab is gated. `null`
  // while the visibility settings are still resolving — treated as hidden
  // so the tab never flashes before the allowlist/boolean lands.
  const jsonConfigVisible = useJsonConfigVisible();
  const showJsonConfigTab = jsonConfigVisible === true;
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  // Get axios instance from apiClient
  const axiosInstance = useMemo(() => apiClient.getAxiosInstance(), []);

  // Clients state
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [selectedClientToAdd, setSelectedClientToAdd] = useState('');
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  // Form state - basic info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deviceType, setDeviceType] = useState<DeviceType>('voice');
  const [priority, setPriority] = useState(50);
  const [isActive, setIsActive] = useState(true);

  // Form state — JSON config fields come from the hardware-type schema
  // (hardware_field_definitions), same source the Create modal uses. This
  // keeps the Edit surface aligned with Create and means any field an
  // admin defines on a hardware type is editable here too — not just the
  // legacy syslog/mqtt/general scalars the form used to hardcode.
  const [schema, setSchema] = useState<HardwareFieldDefinition[] | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [password, setPassword] = useState('');
  const [generatePassword, setGeneratePassword] = useState(false);

  // Auth for password reveal
  const { user } = useAuth();
  const canRevealPassword = user?.roles?.includes('SuperAdmin') || user?.roles?.includes('Admin') || false;

  // Device-specific custom fields
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  // Fetch template data when modal opens
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!isOpen || !templateId) return;

      setIsFetching(true);
      setError(null);

      try {
        const response = await axiosInstance.get(`/v2/config-templates/${templateId}`);
        const data = response.data;

        const templateData: ConfigTemplate = {
          id: String(data.id),
          name: data.name,
          description: data.description,
          client_id: data.client_id || '00000000-0000-0000-0000-000000000000',
          client_ids: data.client_ids || [data.client_id || '00000000-0000-0000-0000-000000000000'],
          device_type: data.device_type,
          priority: data.priority ?? 50,
          json_config: data.json_config || {},
          is_active: data.is_active,
          created_at: data.created_at,
          updated_at: data.updated_at
        };

        setTemplate(templateData);
        initializeFormFromTemplate(templateData);
      } catch (err: any) {
        console.error('[CONFIG TEMPLATE DETAIL] Error fetching template:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load template');
      } finally {
        setIsFetching(false);
      }
    };

    fetchTemplate();
  }, [isOpen, templateId, axiosInstance]);

  // Fetch the clients that own ≥1 device of the template's deviceType —
  // same source as the Create modal so the targeting combobox shows the
  // same list. When deviceType changes (admin switches the template's
  // target type) we prune specific selections that no longer match,
  // preserving the global sentinel.
  const [clientsLoading, setClientsLoading] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    if (!deviceType) {
      setClients([]);
      return;
    }
    let cancelled = false;
    setClientsLoading(true);
    clientService
      .fetchClientsByDeviceType(deviceType)
      .then((list) => {
        if (cancelled) return;
        setClients(list);
        setClientIds((prev) => {
          if (prev.includes(DEFAULT_CLIENT_ID)) return prev;
          const validIds = new Set(list.map((c) => c.id));
          const next = prev.filter((id) => validIds.has(id));
          return next.length === prev.length ? prev : next;
        });
      })
      .catch((err) => {
        if (!cancelled) console.error('[CONFIG TEMPLATE DETAIL] fetch clients:', err);
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, deviceType]);

  const initializeFormFromTemplate = (tmpl: ConfigTemplate) => {
    setName(tmpl.name);
    setDescription(tmpl.description || '');
    setSelectedClientId(tmpl.client_id);
    setClientIds(tmpl.client_ids || [tmpl.client_id]);
    setDeviceType(tmpl.device_type);
    setPriority(tmpl.priority);
    setIsActive(tmpl.is_active);

    // Prefill fieldValues from the stored json_config. The schema-driven
    // form is wired below via a useEffect keyed on deviceType + schema;
    // here we just seed every scalar key so the inputs show current
    // values even before the schema resolves. password + mqtt_pass are
    // deliberately NOT surfaced (the user must re-enter to change them).
    const config = tmpl.json_config || {};
    const prefill: Record<string, string> = {};
    for (const [key, val] of Object.entries(config)) {
      if (key === 'custom_fields' || key === 'password' || key === 'mqtt_pass') continue;
      if (val === null || val === undefined) continue;
      prefill[key] = typeof val === 'object' ? JSON.stringify(val) : String(val);
    }
    setFieldValues(prefill);
    setPassword(''); // Don't prefill password for security
    setGeneratePassword(config.generate_password === true);

    // Initialize custom fields from template. Device-type defaults used
    // to be merged here via typesService.getConfigTypes(), but that API
    // no longer exists — defaults now live on the hardware-type schema
    // (field_definitions) and are surfaced by the config-assembly
    // pipeline at read time.
    const templateCustomFields = (config.custom_fields || []).map((f: CustomField) => ({
      ...f,
      source: 'template' as const,
    }));
    setCustomFields(templateCustomFields);
  };

  // Fetch the hardware-type schema whenever deviceType changes. Mirrors
  // CreateConfigTemplateModal so Edit can render the same schema-driven
  // form. Unknown json_config keys (those not in the schema) still exist
  // in fieldValues from initializeFormFromTemplate and are preserved on
  // save via buildUpdatePayload — they just won't have a dedicated input.
  useEffect(() => {
    if (!isOpen) return;
    if (!deviceType) {
      setSchema([]);
      return;
    }
    let cancelled = false;
    setSchemaLoading(true);
    typesService
      .getHardwareTypes(true)
      .then((types) => {
        const hw = types.find((tt) => tt.code === deviceType);
        if (!hw) {
          if (!cancelled) setSchema([]);
          return null;
        }
        return typesService.getHardwareFieldDefinitions(hw.id);
      })
      .then((response) => {
        if (cancelled || !response) return;
        const sorted = [...response.items].sort((a, b) => {
          const groupCmp = (a.grouping_name || 'General').localeCompare(b.grouping_name || 'General');
          if (groupCmp !== 0) return groupCmp;
          return (a.display_order || 0) - (b.display_order || 0);
        });
        setSchema(sorted);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[CONFIG TEMPLATE DETAIL] Failed to fetch schema:', err);
          setSchema([]);
        }
      })
      .finally(() => {
        if (!cancelled) setSchemaLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, deviceType]);

  const setFieldValue = (fieldName: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    }
  };

  const parseOptions = (opt: unknown): string[] => {
    if (!opt) return [];
    if (Array.isArray(opt)) return opt.map(String);
    if (typeof opt === 'string') {
      try {
        const parsed = JSON.parse(opt);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch { /* noop */ }
    }
    return [];
  };

  const hasPasswordField = useMemo(
    () => !!schema?.some((def) => def.field_name === 'password'),
    [schema],
  );

  const groupedSchema: Array<{ name: string; fields: HardwareFieldDefinition[] }> = useMemo(() => {
    if (!schema) return [];
    const byGroup = new Map<string, HardwareFieldDefinition[]>();
    for (const def of schema) {
      if (def.field_name === 'password') continue; // rendered by Device Password section
      const g = def.grouping_name || 'General';
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g)!.push(def);
    }
    return Array.from(byGroup.entries())
      .map(([name, fields]) => ({ name, fields }))
      .filter((g) => g.fields.length > 0);
  }, [schema]);

  const renderFieldRow = (def: HardwareFieldDefinition): React.ReactNode => {
    const value = fieldValues[def.field_name] ?? '';
    const error = errors[def.field_name];
    const placeholder = def.placeholder || '—';

    let control: React.ReactNode;
    switch (def.value_type) {
      case 'boolean':
        control = (
          <label className="cell-checkbox" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
            <input
              type="checkbox"
              disabled={isLoading}
              checked={value === 'true'}
              onChange={(e) => setFieldValue(def.field_name, e.target.checked ? 'true' : 'false')}
            />
            <span style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary, #6b7280)' }}>
              {value === 'true' ? 'Yes' : 'No'}
            </span>
          </label>
        );
        break;
      case 'password':
        control = <input type="password" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={isLoading} onChange={(e) => setFieldValue(def.field_name, e.target.value)} />;
        break;
      case 'port':
      case 'number':
        control = <input type="number" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={isLoading} min={def.min_value ?? undefined} max={def.max_value ?? undefined} onChange={(e) => setFieldValue(def.field_name, e.target.value)} />;
        break;
      case 'dropdown':
        control = (
          <select aria-label={def.field_name} className="cell-select" value={value} disabled={isLoading} onChange={(e) => setFieldValue(def.field_name, e.target.value)}>
            <option value="">{placeholder}</option>
            {parseOptions(def.options).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
        break;
      default:
        control = <input type="text" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={isLoading} onChange={(e) => setFieldValue(def.field_name, e.target.value)} />;
    }

    return (
      <tr key={def.id} className={error ? 'row-invalid' : ''}>
        <td><code>{def.field_name}</code></td>
        <td><span className="type-badge" style={{ backgroundColor: getTypeBadgeColor(def.value_type) }}>{def.value_type}</span></td>
        <td>{control}{error && <div className="field-error" style={{ fontSize: '0.7rem', marginTop: 2 }}>{error}</div>}</td>
      </tr>
    );
  };

  // Helper to get client name by ID
  const getClientName = (clientId: string): string => {
    if (clientId === '00000000-0000-0000-0000-000000000000') {
      return 'StrongBlue';
    }
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown';
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsEditMode(false);
      setError(null);
      setSuccessMessage(null);
      setErrors({});
      setActiveTab('overview');
      setTemplate(null);
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Template name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Template name must be at least 2 characters';
    }

    if (priority < 1 || priority > 100) {
      newErrors.priority = 'Priority must be between 1 and 100';
    }

    if (clientIds.length === 0) {
      newErrors.client_ids = 'Select at least one client, or "All clients"';
    }

    // Templates are partial overlays: blank means "do not override". Validate
    // only the *format* of the values the user actually typed, not their
    // presence. Mirrors CreateConfigTemplateModal.
    if (schema) {
      for (const def of schema) {
        const raw = (fieldValues[def.field_name] ?? '').toString();
        if (!raw.trim()) continue;
        if (def.value_type === 'port' || def.field_name.endsWith('_port')) {
          const n = Number(raw);
          if (isNaN(n) || n < 1 || n > 65535) {
            newErrors[def.field_name] = `${def.display_name} must be between 1 and 65535`;
          }
        }
        if (def.value_type === 'number') {
          const n = Number(raw);
          if (isNaN(n)) {
            newErrors[def.field_name] = `${def.display_name} must be a number`;
          } else {
            if (def.min_value != null && n < def.min_value) {
              newErrors[def.field_name] = `${def.display_name} minimum is ${def.min_value}`;
            }
            if (def.max_value != null && n > def.max_value) {
              newErrors[def.field_name] = `${def.display_name} maximum is ${def.max_value}`;
            }
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Build the PATCH payload from the current edit form state.
   * Extracted so the normal Save button and the impact-preview
   * "confirm" path serialize the template identically.
   */
  const buildUpdatePayload = (): { updateData: ConfigTemplateUpdate; jsonConfig: JsonConfig } => {
    // Start from a shallow copy of the stored json_config so any keys the
    // schema doesn't cover are preserved on save instead of being dropped.
    // Schema-defined fields then overwrite whatever fieldValues the user
    // edited. custom_fields + password get their own special handling below.
    const jsonConfig: JsonConfig = { ...(template?.json_config || {}) };
    delete (jsonConfig as Record<string, unknown>).custom_fields;

    if (schema) {
      for (const def of schema) {
        if (def.field_name === 'password') continue; // handled below
        const raw = fieldValues[def.field_name];
        if (raw === undefined || raw === null || raw === '') {
          // Blank = "clear this field in the template"; drop the key so the
          // stored payload doesn't carry a stale value.
          delete (jsonConfig as Record<string, unknown>)[def.field_name];
          continue;
        }
        if (def.value_type === 'port' || def.value_type === 'number') {
          const n = Number(raw);
          (jsonConfig as Record<string, unknown>)[def.field_name] = Number.isNaN(n) ? raw : n;
        } else if (def.value_type === 'boolean') {
          (jsonConfig as Record<string, unknown>)[def.field_name] = raw === 'true';
        } else {
          (jsonConfig as Record<string, unknown>)[def.field_name] = raw;
        }
      }
    }

    // Password — mirror Create: generate_password toggle + optional explicit
    // value. Blank password with generate_password=false keeps the existing
    // value (backend leaves password untouched when the key is absent).
    jsonConfig.generate_password = generatePassword;
    if (generatePassword) {
      delete (jsonConfig as Record<string, unknown>).password;
    } else if (password.trim()) {
      jsonConfig.password = password.trim();
    }

    if (customFields.length > 0) {
      jsonConfig.custom_fields = customFields;
    } else {
      delete (jsonConfig as Record<string, unknown>).custom_fields;
    }
    const updateData: ConfigTemplateUpdate = {
      name: name.trim(),
      description: description.trim() || undefined,
      client_id: clientIds[0], // back-compat: first in list
      client_ids: clientIds,
      device_type: deviceType,
      priority: priority,
      is_active: isActive,
      json_config: jsonConfig,
    };
    return { updateData, jsonConfig };
  };

  const persistTemplate = async () => {
    if (!template) return;
    const { updateData } = buildUpdatePayload();
    await axiosInstance.patch(`/v2/config-templates/${template.id}`, updateData);
    setSuccessMessage('Template updated successfully');
    setIsEditMode(false);
    onTemplateUpdated();
  };

  const handleSave = async () => {
    if (!template) return;
    setError(null);
    setSuccessMessage(null);
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      await persistTemplate();
    } catch (err: any) {
      console.error('[CONFIG TEMPLATE DETAIL] Error updating template:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update template';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = () => {
    if (!template) return;
    setError(null);
    setSuccessMessage(null);
    if (!validateForm()) return;
    setShowPreview(true);
  };

  const handleConfirmFromPreview = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await persistTemplate();
      setShowPreview(false);
    } catch (err: any) {
      console.error('[CONFIG TEMPLATE DETAIL] Error updating template:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update template';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (template) {
      initializeFormFromTemplate(template);
    }
    setIsEditMode(false);
    setError(null);
    setErrors({});
  };

  // Client management handlers
  const handleAddClient = async () => {
    if (!selectedClientToAdd || !template) return;
    setIsLoadingClients(true);
    setError(null);
    try {
      const result = await typesService.addConfigTemplateClient(parseInt(template.id, 10), selectedClientToAdd);
      setClientIds(result.client_ids);
      setSelectedClientToAdd('');
      setSuccessMessage('Client added successfully');
      onTemplateUpdated();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to add client';
      setError(errorMessage);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const handleRemoveClient = async (clientIdToRemove: string) => {
    if (!template) return;
    if (clientIds.length <= 1) {
      setError('Cannot remove the last client. At least one client must remain.');
      return;
    }
    setIsLoadingClients(true);
    setError(null);
    try {
      const result = await typesService.removeConfigTemplateClient(parseInt(template.id, 10), clientIdToRemove);
      setClientIds(result.client_ids);
      setSuccessMessage('Client removed successfully');
      onTemplateUpdated();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to remove client';
      setError(errorMessage);
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Get clients that can be added (not already associated)
  const availableClientsToAdd = clients.filter(
    c => !clientIds.some(id => id.replace(/-/g, '').toLowerCase() === c.id.replace(/-/g, '').toLowerCase())
  );

  const getPriorityColor = () => {
    return getPriorityLevel(priority).color;
  };

  if (!isOpen) return null;

  if (isFetching) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="config-template-detail-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Loading Template...</h2>
            <button className="close-button" onClick={onClose} aria-label={t('common.close')}>&times;</button>
          </div>
          <div className="modal-content" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner"></div>
            <p>Loading template details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="config-template-detail-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Error</h2>
            <button className="close-button" onClick={onClose} aria-label={t('common.close')}>&times;</button>
          </div>
          <div className="modal-content">
            <div className="error-message">{error || 'Template not found'}</div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'voice': return 'type-badge voice';
      case 'local_alarm': return 'type-badge alarm';
      case 'dtmf_server': return 'type-badge dtmf';
      default: return 'type-badge';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="config-template-detail-modal" data-testid="modal-config-template-detail" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>{isEditMode ? 'Edit Template' : template.name}</h2>
            <span className={getTypeBadgeClass(template.device_type)}>
              {template.device_type.replace('_', ' ')}
            </span>
            <span
              style={{
                marginLeft: '8px',
                padding: '2px 8px',
                backgroundColor: getPriorityLevel(template.priority).color,
                color: '#fff',
                borderRadius: '4px',
                fontSize: '12px'
              }}
            >
              Priority: {template.priority}
            </span>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            data-testid="tab-template-overview"
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab-button ${activeTab === 'config' ? 'active' : ''}`}
            data-testid="tab-template-config"
            onClick={() => setActiveTab('config')}
          >
            Configuration
          </button>
          <button
            className={`tab-button ${activeTab === 'clients' ? 'active' : ''}`}
            data-testid="tab-template-clients"
            onClick={() => setActiveTab('clients')}
          >
            Clients ({clientIds.length})
          </button>
          {showJsonConfigTab && (
            <button
              className={`tab-button ${activeTab === 'json' ? 'active' : ''}`}
              data-testid="tab-template-json"
              onClick={() => setActiveTab('json')}
            >
              JSON Config
            </button>
          )}
          {canViewAuditLog && (
            <button
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
              data-testid="template-history-tab-button"
            >
              History
            </button>
          )}
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">{error}</div>
          )}

          {successMessage && (
            <div className="success-banner">
              <span>{successMessage}</span>
              <button onClick={() => setSuccessMessage(null)}>&times;</button>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="tab-content">
              {isEditMode ? (
                <div className="form-section">
                  <div className="form-group">
                    <label htmlFor="editName">Template Name *</label>
                    <input
                      type="text"
                      id="editName"
                      data-testid="field-template-name-edit"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={errors.name ? 'error' : ''}
                    />
                    {errors.name && <span className="field-error" data-testid="error-template-name">{errors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="editDescription">Description</label>
                    <textarea
                      id="editDescription"
                      data-testid="field-template-description-edit"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="editDeviceType">Device Type</label>
                      <DeviceTypeSelector
                        id="editDeviceType"
                        value={deviceType}
                        onChange={(value) => {
                          setDeviceType(value as DeviceType);
                          if (!value) {
                            setCustomFields([]);
                          }
                        }}
                        includeEmpty={false}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="editStatus">Status</label>
                      <select
                        id="editStatus"
                        value={isActive ? 'active' : 'inactive'}
                        onChange={(e) => setIsActive(e.target.value === 'active')}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="editClientTargeting">
                        {t('configTemplates.targetingLabel')} *
                      </label>
                      <ClientTargetingInput
                        id="editClientTargeting"
                        value={clientIds}
                        onChange={(next) => {
                          setClientIds(next);
                          if (errors.client_ids) {
                            setErrors((prev) => ({ ...prev, client_ids: undefined }));
                          }
                        }}
                        clients={clients}
                        error={!!errors.client_ids}
                        disabled={!deviceType || clientsLoading}
                        disabledHint={
                          !deviceType
                            ? t('configTemplates.targetingPickDeviceTypeFirst')
                            : t('configTemplates.targetingLoadingClients')
                        }
                      />
                      {errors.client_ids && (
                        <span className="field-error">{errors.client_ids}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="editPriority">
                      Priority (1-100)
                      <span
                        style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          backgroundColor: getPriorityColor(),
                          color: '#fff',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        {getPriorityLevel(priority).label}
                      </span>
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="number"
                        id="editPriority"
                        data-testid="field-template-priority-edit"
                        min="1"
                        max="100"
                        value={priority}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val >= 1 && val <= 100) {
                            setPriority(val);
                          }
                        }}
                        style={{ width: '70px', textAlign: 'center' }}
                        className={errors.priority ? 'error' : ''}
                      />
                      <select
                        value={[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].includes(priority) ? priority : ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            setPriority(parseInt(e.target.value, 10));
                          }
                        }}
                        style={{ flex: 1 }}
                      >
                        <option value="">Quick select...</option>
                        <option value="10">10 - Very Low</option>
                        <option value="20">20 - Low</option>
                        <option value="30">30 - Below Normal</option>
                        <option value="40">40 - Normal Low</option>
                        <option value="50">50 - Normal</option>
                        <option value="60">60 - Normal High</option>
                        <option value="70">70 - Above Normal</option>
                        <option value="80">80 - High</option>
                        <option value="90">90 - Very High</option>
                        <option value="100">100 - Critical</option>
                      </select>
                    </div>
                    {errors.priority && <span className="field-error">{errors.priority}</span>}
                    <small
                      style={{
                        display: 'block',
                        marginTop: 4,
                        color: '#5f6368',
                        fontSize: 12,
                        lineHeight: 1.4,
                      }}
                    >
                      Templates are merged in ascending order (1 → 100). A template
                      with priority 100 overrides conflicting values from
                      lower-priority templates. Devices see the result of all
                      matching templates, not just one.
                    </small>
                  </div>
                </div>
              ) : (
                <div className="info-grid">
                  <div className="info-item">
                    <label>Client</label>
                    <span>{getClientName(template.client_id)}</span>
                  </div>
                  <div className="info-item">
                    <label>Device Type</label>
                    <span className={getTypeBadgeClass(template.device_type)}>
                      {template.device_type.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Priority</label>
                    <span
                      style={{
                        padding: '2px 8px',
                        backgroundColor: getPriorityLevel(template.priority).color,
                        color: '#fff',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    >
                      {template.priority} - {getPriorityLevel(template.priority).label}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
                      {template.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </div>
                  <div className="info-item full-width">
                    <label>Description</label>
                    <span>{template.description || 'No description provided'}</span>
                  </div>
                  <div className="info-item">
                    <label>Created</label>
                    <span>{formatDateTime(template.created_at)}</span>
                  </div>
                  <div className="info-item">
                    <label>{t('configTemplates.lastUpdated')}</label>
                    <span>{formatDateTime(template.updated_at)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'config' && (
            <div className="tab-content">
              {isEditMode ? (
                <>
                  {schemaLoading && (
                    <div className="empty-fields"><p>Loading schema for {deviceType}…</p></div>
                  )}
                  {!schemaLoading && schema && schema.length === 0 && (
                    <div className="empty-fields">
                      <p>
                        No field schema registered for <strong>{deviceType}</strong>.
                        Define fields on this hardware type to make them editable here.
                      </p>
                    </div>
                  )}
                  {!schemaLoading && groupedSchema.length > 0 && (
                    <div className="field-defs-list">
                      {groupedSchema.map((group) => (
                        <div className="field-group" key={group.name}>
                          <h4 className="group-header">{group.name}</h4>
                          <table className="field-defs-table">
                            <thead>
                              <tr>
                                <th>{t('devices.fieldName')}</th>
                                <th>Type</th>
                                <th>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.fields.map((def) => renderFieldRow(def))}
                            </tbody>
                          </table>
                        </div>
                      ))}

                      {hasPasswordField && (
                        <div className="field-group">
                          <h4 className="group-header">{t('configTemplates.devicePassword')}</h4>
                          <table className="field-defs-table">
                            <thead><tr><th>Field</th><th>Type</th><th>Value</th></tr></thead>
                            <tbody>
                              <tr>
                                <td><code>generate_password</code></td>
                                <td><span className="type-badge" style={{ backgroundColor: getTypeBadgeColor('boolean') }}>boolean</span></td>
                                <td>
                                  <label className="cell-checkbox" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={generatePassword}
                                      onChange={(e) => {
                                        setGeneratePassword(e.target.checked);
                                        if (e.target.checked) setPassword('');
                                      }}
                                    />
                                    <span style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary, #6b7280)' }}>
                                      {t('configTemplates.randomPerDevice')}
                                    </span>
                                  </label>
                                </td>
                              </tr>
                              {!generatePassword && (
                                <tr>
                                  <td><code>password</code></td>
                                  <td><span className="type-badge" style={{ backgroundColor: getTypeBadgeColor('password') }}>password</span></td>
                                  <td>
                                    <input
                                      type="text"
                                      className="cell-input mono"
                                      value={password}
                                      onChange={(e) => setPassword(e.target.value)}
                                      placeholder={t('configTemplates.leaveEmptyKeepCurrent')}
                                    />
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: '1.25rem' }}>
                    <DeviceSpecificFields
                      fields={customFields}
                      onChange={setCustomFields}
                      isEditing={true}
                    />
                  </div>
                </>
              ) : (
                <ConfigFieldsTable
                  jsonConfig={template.json_config}
                  customFields={customFields}
                  canRevealPassword={canRevealPassword}
                />
              )}
            </div>
          )}

          {activeTab === 'clients' && (
            <div className="tab-content">
              <div className="clients-section">
                <h3>{t('configTemplates.associatedClients')}</h3>
                <p className="section-description">
                  This template is available to the following clients. At least one client must remain.
                </p>
                {clientIds.length > 0 ? (
                  <ul className="clients-list">
                    {clientIds.map((clientId) => {
                      const displayName = getClientName(clientId);
                      const isGlobal = clientId === '00000000-0000-0000-0000-000000000000';
                      return (
                        <li key={clientId} className="client-item">
                          <div className="client-info">
                            <span className="client-name">
                              {displayName}
                              {isGlobal && <span className="global-badge">Global</span>}
                            </span>
                            <span className="client-id">{clientId}</span>
                          </div>
                          {isSuperAdmin && clientIds.length > 1 && (
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => handleRemoveClient(clientId)}
                              disabled={isLoadingClients}
                            >
                              Remove
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="no-clients">No clients associated.</p>
                )}
              </div>

              {isSuperAdmin && (
                <div className="add-client-section">
                  <h3>Add Client</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Client</label>
                      <select
                        value={selectedClientToAdd}
                        onChange={(e) => setSelectedClientToAdd(e.target.value)}
                        disabled={isLoadingClients}
                      >
                        <option value="">Select a client...</option>
                        {availableClientsToAdd.map(client => (
                          <option key={client.id} value={client.id}>{client.name}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={handleAddClient}
                      disabled={isLoadingClients || !selectedClientToAdd}
                    >
                      {isLoadingClients ? 'Adding...' : 'Add Client'}
                    </button>
                  </div>
                  {availableClientsToAdd.length === 0 && clients.length > 0 && (
                    <p className="no-clients-hint">This template is already associated with all available clients.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'json' && showJsonConfigTab && (
            <div className="tab-content">
              <div className="info-section">
                <h4>{t('configTemplates.rawJsonConfiguration')}</h4>
                <pre className="json-config-pre">
                  {JSON.stringify(template.json_config, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {activeTab === 'history' && template && (
            <div className="tab-content">
              <AuditHistoryTab
                resourceType="config_template"
                resourceId={String(template.id)}
              />
            </div>
          )}
        </div>

        <div className="modal-actions">
          {isEditMode ? (
            <>
              <button
                className="btn btn-secondary"
                data-testid="btn-template-cancel"
                onClick={handleCancel}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-secondary"
                data-testid="btn-template-preview"
                onClick={handlePreview}
                disabled={isLoading}
                title={t('templateImpact.subtitle')}
              >
                {t('templateImpact.previewButton')}
              </button>
              <button
                className="btn btn-primary"
                data-testid="btn-template-save"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? t('common.save') + '…' : t('common.save')}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" data-testid="btn-template-close" onClick={onClose}>
                {t('common.close')}
              </button>
              {canEdit && (
                <button
                  className="btn btn-primary"
                  data-testid="btn-template-edit"
                  onClick={() => setIsEditMode(true)}
                >
                  {t('common.edit')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <TemplateImpactPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onConfirm={handleConfirmFromPreview}
        confirming={isLoading}
        request={
          showPreview && template
            ? {
                template_id: Number(template.id),
                name: name.trim() || template.name,
                device_type: deviceType,
                priority: priority,
                json_config: buildUpdatePayload().jsonConfig as Record<string, unknown>,
                client_ids: selectedClientId ? [selectedClientId] : null,
              }
            : null
        }
      />
    </div>
  );
};
